import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { setGlobalConfig } from 'actions/global.config.action'
import { GlobalConfigType } from 'actions/global.config.type'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { ILoanStatusConfig } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAdminConfig, setAdminConfig } from 'services'
import { MultiSelect } from 'stories/components'
import { confirm, generateUniqeId, prompt } from 'utils'

export interface ILoanSubStatusItem {
  id: string
  title: string
  fromPermission: string[]
  toPermission: string[]
  order: number
}

interface IProps {
  /** Parent loan status config map. Sub-status rows are only shown for entries where `use === true`. */
  data: Record<string, ILoanStatusConfig>
}

/**
 * Sub-Status configuration section that appears at the bottom of the Origination tab.
 *
 * Behavior:
 * - Only renders a row per loan status whose `use` flag is true ("IN USE").
 * - Each row exposes a `+` button to add a sub-status. Each sub-status has a title, from/to permissions, order arrows and delete.
 * - Config is persisted to the `loanSubStatus` admin config (LOAN_SUB_STATUS_LABEL) and synced to redux so the rest of the app
 *   (loan banner dropdown, pipeline column, reports) sees changes without a refresh.
 */
export const LoanSubStatusConfig = ({ data: loanStatusData }: IProps) => {
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(false)
  const [config, setConfig] = useState<Record<string, ILoanSubStatusItem[]>>({})

  useEffect(() => {
    setLoading(true)
    getAdminConfig('loanSubStatus')
      .then((res) => setConfig(res || {}))
      .finally(() => setLoading(false))
  }, [])

  const persist = async (next: Record<string, ILoanSubStatusItem[]>) => {
    setLoading(true)
    try {
      await setAdminConfig('loanSubStatus', next)
      setConfig(next)
      dispatch(setGlobalConfig(GlobalConfigType.LoanSubStatus, next))
    } finally {
      setLoading(false)
    }
  }

  const onAdd = async (parentKey: string) => {
    const title = await prompt('Please enter the new sub-status title')
    if (!title) return
    const existingList = config[parentKey] || []
    const id = generateUniqeId(
      title,
      existingList.map((item) => item.id),
    )
    if (!id) return
    const next = cloneDeep(config)
    if (!next[parentKey]) next[parentKey] = []
    next[parentKey].push({
      id,
      title,
      fromPermission: Object.keys(accountTypes),
      toPermission: Object.keys(accountTypes),
      order: existingList.length,
    })
    await persist(next)
  }

  const onChangeField = (
    parentKey: string,
    index: number,
    field: keyof ILoanSubStatusItem,
    value: any,
    saveImmediately = false,
  ) => {
    const next = cloneDeep(config)
    if (!next[parentKey]?.[index]) return
    ;(next[parentKey][index] as any)[field] = value
    if (saveImmediately) {
      void persist(next)
    } else {
      setConfig(next)
    }
  }

  const onBlurSave = async () => {
    await persist(config)
  }

  const onRemove = async (parentKey: string, index: number) => {
    const ok = await confirm('Are you sure you want to remove this sub-status?')
    if (!ok) return
    const next = cloneDeep(config)
    next[parentKey].splice(index, 1)
    next[parentKey] = next[parentKey].map((item, i) => ({ ...item, order: i }))
    await persist(next)
  }

  const onReorder = async (parentKey: string, fromIndex: number, toIndex: number) => {
    const list = config[parentKey] || []
    if (toIndex < 0 || toIndex >= list.length) return
    const next = cloneDeep(config)
    const [moved] = next[parentKey].splice(fromIndex, 1)
    next[parentKey].splice(toIndex, 0, moved)
    next[parentKey] = next[parentKey].map((item, i) => ({ ...item, order: i }))
    await persist(next)
  }

  // Statuses marked "IN USE" - preserve order from the parent table
  const inUseStatuses = Object.keys(loanStatusData || {}).filter((key) => loanStatusData[key]?.use)

  return (
    <div className="relative mt-4">
      <LayoutLoading show={isLoading} />
      <div className={`border-b-2 border-shade-blue w-full italic`}>
        <p>2. Loan Sub-Status</p>
      </div>
      <p className="text-xs text-gray-600 mt-1 mb-3">
        Add optional sub-statuses to any status marked "IN USE". When at least one sub-status exists, a Sub-Status
        dropdown will appear on each loan and can be added as a Pipeline column.
      </p>

      {inUseStatuses.length === 0 ? (
        <div className="text-sm text-gray-500 px-2 py-3">No statuses are marked "IN USE" yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Loan Status / Sub-Status', 'From', 'To', 'Order', 'Action'].map((v) => (
                <th scope="col" className="p-3 border" key={`sub-header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {inUseStatuses.map((parentKey) => {
              const parentLabel = loanStatusData[parentKey].new
              const list = config[parentKey] || []
              return (
                <Fragment key={parentKey}>
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-3 py-2 border">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{parentLabel}</p>
                        <button
                          className="flex items-center gap-1 text-shade-blue hover:underline text-sm"
                          onClick={() => onAdd(parentKey)}
                        >
                          <PlusIcon className="w-4 h-4" /> Add
                        </button>
                      </div>
                    </td>
                  </tr>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-2 border text-xs italic text-gray-500 text-center">
                        No sub-statuses yet.
                      </td>
                    </tr>
                  ) : (
                    list.map((item, index) => (
                      <tr key={item.id}>
                        <td className="p-2 border text-center">{index + 1}</td>
                        <td className="px-2 border">
                          <PlainInput
                            value={item.title}
                            content={item.title}
                            onChange={(value: string) => onChangeField(parentKey, index, 'title', value, true)}
                          />
                        </td>
                        <td className="px-2 border text-center">
                          <MultiSelect
                            id={`sub-${parentKey}-${item.id}-from`}
                            title="Account Type"
                            options={accountTypes}
                            value={item.fromPermission || []}
                            onChange={(selected) => onChangeField(parentKey, index, 'fromPermission', selected)}
                            onBlur={onBlurSave}
                            className="w-64"
                          />
                        </td>
                        <td className="px-2 border text-center">
                          <MultiSelect
                            id={`sub-${parentKey}-${item.id}-to`}
                            title="Account Type"
                            options={accountTypes}
                            value={item.toPermission || []}
                            onChange={(selected) => onChangeField(parentKey, index, 'toPermission', selected)}
                            onBlur={onBlurSave}
                            className="w-64"
                          />
                        </td>
                        <td className="px-2 border text-center">
                          <select
                            value={index}
                            className="text-sm py-1 border-gray-200 w-16"
                            onChange={(e) => onReorder(parentKey, index, parseInt(e.target.value))}
                          >
                            {list.map((_, i) => (
                              <option value={i} key={i}>
                                {i + 1}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 border text-center">
                          <div className="flex justify-center">
                            <span className="btn-icon" onClick={() => onRemove(parentKey, index)}>
                              <TrashIcon className="w-4 h-4 text-red-500" />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
